/**
 * Shared SPA route -> SEO config map.
 *
 * This module is imported by BOTH server (spaSeoInject.ts) and client
 * (spaDocumentSeo.ts) code, so it must not depend on any server-only
 * or client-only module. Public brand identity comes from the shared,
 * environment-independent brand config.
 */

import { brandConfig } from '../../config/brandConfig';

/**
 * JSON-LD generation strategy for a SPA route.
 * - "website"  -> WebSite + Organization (home page)
 * - "webpage"  -> WebPage (generic indexable page)
 * - "topics"   -> ItemList of public courses from D1 (browse page)
 * - "github"   -> ItemList of current GitHub Trending repositories from D1
 * - "none"     -> no structured data (noindex pages)
 */
export type JsonLdStrategy = 'website' | 'webpage' | 'topics' | 'github' | 'none';

/**
 * <noscript> first-paint content strategy for a SPA route.
 * - "static" -> brand hero text + key navigation links (no D1 query)
 * - "topics" -> course title + link list from D1
 * - "github" -> current repository details + generated Topic links from D1
 * - "none"   -> empty (noindex / private pages)
 */
export type NoscriptStrategy = 'static' | 'topics' | 'github' | 'none';

export interface SpaSeoConfig {
	title: string;
	description: string;
	ogType: 'website' | 'article';
	robots: 'index,follow' | 'noindex,nofollow' | 'noindex,nofollow,noarchive';
	jsonLd: JsonLdStrategy;
	noscript: NoscriptStrategy;
}

const BRAND_NAME = brandConfig.name;

/**
 * Unified SPA route -> SEO config map.
 *
 * Adding a new SPA route only requires one row here - no new rendering
 * code. `spaFallback.ts` reads this table, `spaSeoInject.ts` renders
 * the head meta (via the shared `renderSeoMetaTags`) and `<noscript>`
 * content based on the strategy fields.
 *
 * The key is the first path segment ("" for "/").
 */
export const SPA_SEO_MAP: Record<string, SpaSeoConfig> = {
	'': {
		title: `${BRAND_NAME} | Learn Any Topic, Chapter by Chapter`,
		description: `Explore curated topics or create private topics from your own sources. Learn through clear chapters you can read, listen to, or watch, with progress synced across devices.`,
		ogType: 'website',
		robots: 'index,follow',
		jsonLd: 'website',
		noscript: 'static',
	},
	browse: {
		title: `Browse Topics | ${BRAND_NAME}`,
		description: `Explore curated topics on AI, engineering, startups, growth, and more-organized into chapters you can read, listen to, or watch.`,
		ogType: 'website',
		robots: 'index,follow',
		jsonLd: 'topics',
		noscript: 'topics',
	},
	community: {
		title: `Community Picks | ${BRAND_NAME}`,
		description: `Support what ${BRAND_NAME} builds next.`,
		ogType: 'website',
		// 2026-09-09 SEO/GEO review: /community is an interaction surface whose
		// proposals graduate into topics, which already have their own indexed
		// landing pages. Serving idea titles to crawlers here only duplicated
		// that content, so the route is de-indexed and out of the sitemap.
		// Idea detail pages were already permanently noindex.
		robots: 'noindex,nofollow',
		jsonLd: 'none',
		noscript: 'none',
	},
	ai: {
		title: `AI Create | ${BRAND_NAME}`,
		description: `Create a private topic from sources you provide with ${BRAND_NAME} AI Create.`,
		ogType: 'website',
		// P2-1 (2026-09-02): /ai is a logged-in tool route served with the app
		// shell's generic body — same boundary as /pricing. Reopen only with a
		// real public explainer page.
		robots: 'noindex,nofollow',
		jsonLd: 'webpage',
		noscript: 'static',
	},
	'github-trending': {
		title: `GitHub Trending | ${BRAND_NAME}`,
		description: `Track notable GitHub repositories and explore selected projects as source-linked ${BRAND_NAME} topics.`,
		ogType: 'website',
		robots: 'index,follow',
		jsonLd: 'github',
		noscript: 'github',
	},
	pricing: {
		title: `Pricing | ${BRAND_NAME}`,
		description: `Compare ${BRAND_NAME} plans for public topics, media modes, and private-topic creation.`,
		ogType: 'website',
		robots: 'noindex,nofollow',
		jsonLd: 'webpage',
		noscript: 'static',
	},
	// Private / personal pages - not indexed
	profile: {
		title: `Profile | ${BRAND_NAME}`,
		description: '',
		ogType: 'website',
		robots: 'noindex,nofollow,noarchive',
		jsonLd: 'none',
		noscript: 'none',
	},
	history: {
		title: `Learning History | ${BRAND_NAME}`,
		description: '',
		ogType: 'website',
		robots: 'noindex,nofollow,noarchive',
		jsonLd: 'none',
		noscript: 'none',
	},
	feedback: {
		title: `Feedback | ${BRAND_NAME}`,
		description: '',
		ogType: 'website',
		robots: 'noindex,nofollow,noarchive',
		jsonLd: 'none',
		noscript: 'none',
	},
	settings: {
		title: `Settings | ${BRAND_NAME}`,
		description: '',
		ogType: 'website',
		robots: 'noindex,nofollow,noarchive',
		jsonLd: 'none',
		noscript: 'none',
	},
	referrals: {
		title: `Invite Friends | ${BRAND_NAME}`,
		description: '',
		ogType: 'website',
		robots: 'noindex,nofollow,noarchive',
		jsonLd: 'none',
		noscript: 'none',
	},
	'membership-billing': {
		title: `Membership & Billing | ${BRAND_NAME}`,
		description: '',
		ogType: 'website',
		robots: 'noindex,nofollow,noarchive',
		jsonLd: 'none',
		noscript: 'none',
	},
};
